import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import { notifyHost } from '../../../lib/notificationDispatch';
import Icon from '../../../components/AppIcon';
import { format } from 'date-fns';
import ToastNotification from './ToastNotification';
import DiffImages, { diffValuesEqual } from '../../../components/DiffImages';

// Fields to show in the diff view (label, key)
const DIFF_FIELDS = [
  { label: 'Title', key: 'title' },
  { label: 'Description', key: 'description' },
  { label: 'Category', key: 'category' },
  { label: 'Location Name', key: 'location_name' },
  { label: 'Location Address', key: 'location_address' },
  { label: 'Start Date', key: 'start_date', isDate: true },
  { label: 'End Date', key: 'end_date', isDate: true },
  { label: 'Registration Deadline', key: 'registration_deadline', isDate: true },
  { label: 'Price', key: 'price' },
  { label: 'Max Participants', key: 'max_participants' },
  { label: 'Min Age', key: 'min_age' },
  { label: 'Max Age', key: 'max_age' },
  { label: 'Event Type', key: 'event_type' },
  { label: 'Difficulty', key: 'difficulty' },
  { label: 'Duration (min)', key: 'duration_minutes' },
  { label: 'Requirements', key: 'requirements' },
  { label: 'What to Bring', key: 'what_to_bring' },
  { label: 'Cancellation Policy', key: 'cancellation_policy' },
  { label: 'Cover Image', key: 'image_url', isImage: true },
  { label: 'Additional Images', key: 'additional_images', isImage: true },
];

const formatValue = (val, isDate) => {
  if (val === null || val === undefined || val === '') return <span className="text-muted-foreground italic">—</span>;
  if (isDate) {
    try { return format(new Date(val), 'MMM d, yyyy h:mm a'); } catch { return String(val); }
  }
  if (typeof val === 'object') return <span className="font-mono text-xs break-all">{JSON.stringify(val)}</span>;
  return String(val);
};

const DiffRow = ({ label, current, proposed, isDate, isImage }) => {
  if (diffValuesEqual(current, proposed)) return null; // Only show changed fields

  return (
    <tr className="border-b border-border last:border-0">
      <td className="py-2.5 px-3 text-xs font-medium text-muted-foreground w-32 align-top">{label}</td>
      <td className="py-2.5 px-3 text-xs text-foreground align-top bg-red-50/50 max-w-[200px]">
        {isImage ? <DiffImages value={current} dimmed /> : <span className="line-through opacity-70">{formatValue(current, isDate)}</span>}
      </td>
      <td className="py-2.5 px-3 text-xs text-foreground align-top bg-green-50/50 max-w-[200px]">
        {isImage ? <DiffImages value={proposed} /> : <span className="font-medium text-green-800">{formatValue(proposed, isDate)}</span>}
      </td>
    </tr>
  );
};

const PendingEditsTab = ({ onCountChange, onEditsResolved, refreshToken }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectModal, setRejectModal] = useState({ open: false, event: null });
  const [rejectReason, setRejectReason] = useState('');
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const showSuccess = (msg) => { setSuccessMessage(msg); setTimeout(() => setSuccessMessage(null), 3500); };
  const showError = (msg) => { setErrorMessage(msg); setTimeout(() => setErrorMessage(null), 4000); };

  const fetchEvents = useCallback(async ({ silent } = {}) => {
    if (!silent) setLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('events')
        ?.select(`
          id, title, description, category, difficulty, image_url, additional_images,
          location_name, location_address, start_date, end_date,
          registration_deadline, price, max_participants, min_age, max_age,
          requirements, what_to_bring, cancellation_policy, event_type,
          duration_minutes, status, host_id, pending_edits, created_at, updated_at,
          event_host_profiles (
            id, organization_name, full_name, email, avatar_url
          )
        `)
        ?.not('pending_edits', 'is', null)
        ?.order('updated_at', { ascending: false });

      if (error) throw error;
      setEvents(data || []);
      onCountChange?.((data || [])?.length);
    } catch (err) {
      showError(err?.message || 'Failed to load pending edits');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [onCountChange]);

  // Initial load (shows the loading spinner).
  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  // Refetch when the parent's manual Refresh button is clicked. This tab
  // stays mounted while the admin is on it, so it otherwise never learns
  // about edits submitted after the initial load. Silent — no spinner.
  useEffect(() => {
    if (!refreshToken) return;
    fetchEvents({ silent: true });
  }, [refreshToken, fetchEvents]);

  // Auto-refresh so a host's live edit shows up without a manual refresh.
  useEffect(() => {
    const intervalId = setInterval(() => fetchEvents({ silent: true }), 30000);
    return () => clearInterval(intervalId);
  }, [fetchEvents]);

  const handleApprove = async (event) => {
    setActionLoading(true);
    try {
      const edits = event?.pending_edits || {};
      // Remove any meta fields that shouldn't be copied
      const { submitted_at, submitted_by, ...fieldsToApply } = edits;

      const { error } = await supabase
        ?.from('events')
        ?.update({
          ...fieldsToApply,
          pending_edits: null,
          edit_rejection_reason: null,
          edit_approved_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString(),
        })
        ?.eq('id', event?.id);

      if (error) throw error;

      const host = event?.event_host_profiles;
      await notifyHost({
        userId: event?.host_id,
        eventId: event?.id,
        notificationType: 'event_edit_review',
        title: 'Event Edits Approved',
        body: `Your edits to "${event?.title}" have been approved and are now live.`,
        emailTemplate: 'admin_notification',
        emailTo: host?.email,
        emailSubject: 'Your KidQuest Event Edits Were Approved',
        emailData: {
          user_name: host?.full_name || 'there',
          heading: 'Edits Approved!',
          emoji: '✅',
          intro: 'The KidQuest team reviewed your edits and they are now live.',
          detail_label: 'Event',
          detail_value: event?.title || 'Your event',
        },
      });

      showSuccess(`Edits for "${event?.title}" approved and applied.`);
      setExpandedId(null);
      fetchEvents();
      onEditsResolved?.();
    } catch (err) {
      showError(err?.message || 'Failed to approve edits');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectSubmit = async () => {
    const event = rejectModal?.event;
    if (!event) return;
    setActionLoading(true);
    try {
      const trimmedReason = rejectReason?.trim();
      const finalReason = trimmedReason || 'Your edits were not approved by the admin.';

      const { error } = await supabase
        ?.from('events')
        ?.update({
          pending_edits: null,
          edit_rejection_reason: finalReason,
          edit_approved_at: null,
          updated_at: new Date()?.toISOString(),
        })
        ?.eq('id', event?.id);

      if (error) throw error;

      const notifBody = trimmedReason
        ? `Your edits to "${event?.title}" were rejected. Reason: ${trimmedReason}`
        : `Your edits to "${event?.title}" were rejected by the admin.`;

      const host = event?.event_host_profiles;
      await notifyHost({
        userId: event?.host_id,
        eventId: event?.id,
        notificationType: 'event_edit_review',
        title: 'Event Edits Rejected',
        body: notifBody,
        emailTemplate: 'admin_notification',
        emailTo: host?.email,
        emailSubject: 'Update on Your KidQuest Event Edits',
        emailData: {
          user_name: host?.full_name || 'there',
          heading: 'Edits Not Approved',
          emoji: '✏️',
          intro: 'The KidQuest team reviewed your edits to this event and did not approve them — the event remains as it was before.',
          detail_label: 'Event',
          detail_value: event?.title || 'Your event',
          detail_label_2: trimmedReason ? 'Reason' : '',
          detail_value_2: trimmedReason || '',
          tone: 'negative',
        },
      });

      showSuccess(`Edits for "${event?.title}" rejected and discarded.`);
      setRejectModal({ open: false, event: null });
      setRejectReason('');
      setExpandedId(null);
      fetchEvents();
      onEditsResolved?.();
    } catch (err) {
      showError(err?.message || 'Failed to reject edits');
    } finally {
      setActionLoading(false);
    }
  };

  const openRejectModal = (event) => {
    setRejectReason('');
    setRejectModal({ open: true, event });
  };

  const getChangedFieldsCount = (event) => {
    const edits = event?.pending_edits || {};
    return DIFF_FIELDS?.filter(({ key }) => {
      const current = event?.[key];
      const proposed = edits?.[key];
      return proposed !== undefined && !diffValuesEqual(current, proposed);
    })?.length;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Loading pending edits...</p>
        </div>
      </div>
    );
  }

  if (events?.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center">
          <Icon name="CheckCircle" size={28} className="text-green-500" />
        </div>
        <p className="text-base font-medium text-foreground">No Pending Edits</p>
        <p className="text-sm text-muted-foreground">All event edit requests have been reviewed.</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {events?.map((event) => {
          const isExpanded = expandedId === event?.id;
          const edits = event?.pending_edits || {};
          const changedCount = getChangedFieldsCount(event);
          const hostName = event?.event_host_profiles?.organization_name || event?.event_host_profiles?.full_name || '—';

          return (
            <div key={event?.id} className="bg-card border border-border rounded-xl overflow-hidden">
              {/* Event Row */}
              <div className="flex items-center gap-4 px-4 py-3">
                {event?.image_url ? (
                  <img
                    src={event?.image_url}
                    alt={event?.title}
                    className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                    <Icon name="Calendar" size={18} className="text-muted-foreground" />
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-foreground truncate">{event?.title}</p>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200 flex-shrink-0">
                      <Icon name="Clock" size={10} />
                      Pending Edits
                    </span>
                    {changedCount > 0 && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-700 flex-shrink-0">
                        {changedCount} field{changedCount !== 1 ? 's' : ''} changed
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Host: {hostName}
                    {edits?.submitted_at && (
                      <span className="ml-2">
                        · Submitted {format(new Date(edits.submitted_at), 'MMM d, yyyy')}
                      </span>
                    )}
                  </p>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleApprove(event)}
                    disabled={actionLoading}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    <Icon name="Check" size={12} />
                    Approve
                  </button>
                  <button
                    onClick={() => openRejectModal(event)}
                    disabled={actionLoading}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    <Icon name="X" size={12} />
                    Reject
                  </button>
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : event?.id)}
                    className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    title={isExpanded ? 'Collapse diff' : 'View diff'}
                  >
                    <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={16} />
                  </button>
                </div>
              </div>
              {/* Diff Panel */}
              {isExpanded && (
                <div className="border-t border-border bg-muted/20 px-4 py-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Icon name="GitCompare" size={14} className="text-muted-foreground" />
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Changes Preview</span>
                  </div>

                  {changedCount === 0 ? (
                    <p className="text-sm text-muted-foreground italic">No field-level differences detected in the pending edits.</p>
                  ) : (
                    <div className="rounded-lg border border-border overflow-hidden">
                      <div className="grid grid-cols-[120px_1fr_1fr] bg-muted/50 border-b border-border">
                        <div className="px-3 py-2 text-xs font-semibold text-muted-foreground">Field</div>
                        <div className="px-3 py-2 text-xs font-semibold text-red-600 bg-red-50/50">Current (Live)</div>
                        <div className="px-3 py-2 text-xs font-semibold text-green-700 bg-green-50/50">Proposed (Edit)</div>
                      </div>
                      <table className="w-full">
                        <tbody>
                          {DIFF_FIELDS?.map(({ label, key, isDate, isImage }) => {
                            const current = event?.[key];
                            const proposed = edits?.[key];
                            if (proposed === undefined) return null;
                            if (diffValuesEqual(current, proposed)) return null;
                            return (
                              <DiffRow
                                key={key}
                                label={label}
                                current={current}
                                proposed={proposed}
                                isDate={isDate}
                                isImage={isImage}
                              />
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Show any extra keys in pending_edits not in DIFF_FIELDS */}
                  {(() => {
                    const knownKeys = new Set([...DIFF_FIELDS.map(f => f.key), 'submitted_at', 'submitted_by']);
                    const extraKeys = Object.keys(edits)?.filter(k => !knownKeys?.has(k));
                    if (extraKeys?.length === 0) return null;
                    return (
                      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-xs font-medium text-blue-700 mb-1">Additional fields in pending edits:</p>
                        <div className="flex flex-wrap gap-1">
                          {extraKeys?.map(k => (
                            <span key={k} className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded font-mono">{k}</span>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* Reject Modal */}
      {rejectModal?.open && rejectModal?.event && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60">
          <div className="bg-white border border-border rounded-2xl w-full max-w-md shadow-xl">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <h3 className="text-base font-semibold text-foreground">Reject Pending Edits</h3>
              <button
                onClick={() => setRejectModal({ open: false, event: null })}
                className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-muted-foreground">
                Rejecting edits for <span className="font-medium text-foreground">"{rejectModal?.event?.title}"</span>. The event will remain unchanged. Optionally provide a reason to notify the host.
              </p>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Reason <span className="text-muted-foreground font-normal">(optional)</span>
                </label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e?.target?.value)}
                  placeholder="Explain why these edits are being rejected..."
                  rows={4}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setRejectModal({ open: false, event: null })}
                  className="flex-1 px-4 py-2.5 text-sm font-medium border border-border rounded-xl hover:bg-muted transition-colors text-foreground"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRejectSubmit}
                  disabled={actionLoading}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {actionLoading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Icon name="XCircle" size={14} />
                  )}
                  Reject Edits
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <ToastNotification message={successMessage} type="success" onClose={() => setSuccessMessage(null)} />
      <ToastNotification message={errorMessage} type="error" onClose={() => setErrorMessage(null)} />
    </>
  );
};

export default PendingEditsTab;
